<template>
  <div class="location-highlights-table">
    <div class="location-highlights-table__heading">
      <h2 class="global-h4 location-highlights-table__title">{{ peopleVisible ? "Locations, Events & People" : "Locations & Events" }}</h2>
      <!-- <span class="location-highlights-table__total">{{ props.rows.length }}</span> -->
    </div>

    <div class="location-highlights-table__toolbar">
      <div class="location-highlights-table__search">
        <IconEmbedded name="magnifier_2-5" :size="16" color="#667085" />
        <input v-model="search" type="text" :aria-label="peopleVisible ? 'Search accounts' : 'Search places and events'" placeholder="Search" />
      </div>

      <LocationCategoryDropdown v-model="selectedCategory" class="location-highlights-table__category" :includePeople="peopleVisible" />

      <SrpDropdown class="location-highlights-table__status" placement="bottom-start" :offset="4" :width="200">
        <template #clickableElement="{ isDropdownVisible }">
          <button type="button" class="location-highlights-table__status-trigger" :aria-label="`Filter by status: ${activeTabLabel}`" :aria-expanded="isDropdownVisible">
            <span>{{ activeTabLabel }}</span>
            <IconEmbedded name="caret-bottom_3" :size="14" color="#667085" />
          </button>
        </template>
        <template #dropdown="{ close, isDropdownVisible }">
          <div class="location-highlights-table__status-menu" :inert="!isDropdownVisible" :aria-hidden="!isDropdownVisible" @keydown.esc.stop="close()">
            <button
              v-for="option in tabOptions"
              :key="option.value"
              type="button"
              :class="{ 'location-highlights-table__status-choice--selected': props.activeTab === option.value }"
              @click="chooseTab(option.value, close)"
            >
              {{ option.label }} <span class="location-highlights-table__status-count">{{ option.count }}</span>
            </button>
          </div>
        </template>
      </SrpDropdown>

      <div class="location-highlights-table__actions">
        <SrpButton fill="outlined" size="small" sidePaddings="small" aria-label="Add a location or event" @click="emit('addLocation')">
          <template #icon><IconEmbedded name="plus_2-5" :size="16" /></template>
          Add new
        </SrpButton>
        <SrpDropdown v-if="props.peopleRows.length > 0 || props.archivedRows.length > 0" class="location-highlights-table__more-dropdown" placement="bottom-end" :offset="4" :width="180">
          <template #clickableElement="{ isDropdownVisible }">
            <button type="button" class="location-highlights-table__more-trigger" aria-label="More list options" :aria-expanded="isDropdownVisible">
              <IconEmbedded name="dots-horizontal_2" :size="20" color="#344054" />
            </button>
          </template>
          <template #dropdown="{ close, isDropdownVisible }">
            <div class="location-highlights-table__more-menu" role="menu" :inert="!isDropdownVisible" :aria-hidden="!isDropdownVisible" @keydown.esc.stop="close()">
              <button v-if="props.peopleRows.length > 0" type="button" role="menuitemcheckbox" :aria-checked="showPeople" @click="toggleShowPeople(close)">
                <span>Show people</span>
                <IconEmbedded v-if="showPeople" name="check_3" :size="16" color="#087f82" />
              </button>
              <button v-if="props.archivedRows.length > 0" type="button" role="menuitemcheckbox" :aria-checked="showArchived" @click="toggleShowArchived(close)">
                <span>Show archived</span>
                <IconEmbedded v-if="showArchived" name="check_3" :size="16" color="#087f82" />
              </button>
            </div>
          </template>
        </SrpDropdown>
      </div>
    </div>

    <table class="location-highlights-table__table">
      <thead>
        <tr class="location-highlights-table__header-row">
          <th class="location-highlights-table__th location-highlights-table__th--sort" :aria-sort="ariaSort('name')" @click="sortByColumn('name')">
            <span class="location-highlights-table__th-inner">
              {{ peopleVisible ? "Location / Event / Person" : "Location / Event" }}
              <IconEmbedded v-if="sortKey === 'name'" class="location-highlights-table__caret" :name="sortCaret" :size="10" />
            </span>
          </th>
          <th class="location-highlights-table__th location-highlights-table__th--photos location-highlights-table__th--sort" :aria-sort="ariaSort('photoCount')" @click="sortByColumn('photoCount')">
            <span class="location-highlights-table__th-inner">
              Photos
              <IconEmbedded v-if="sortKey === 'photoCount'" class="location-highlights-table__caret" :name="sortCaret" :size="10" />
            </span>
          </th>
          <th class="location-highlights-table__th location-highlights-table__th--action"><span class="location-highlights-table__sr-only">Actions</span></th>
          <th class="location-highlights-table__th location-highlights-table__th--posts location-highlights-table__th--sort" :aria-sort="ariaSort('postCount')" @click="sortByColumn('postCount')">
            <span class="location-highlights-table__th-inner">
              Posts
              <IconEmbedded v-if="sortKey === 'postCount'" class="location-highlights-table__caret" :name="sortCaret" :size="10" />
            </span>
          </th>
          <th
            class="location-highlights-table__th location-highlights-table__th--date location-highlights-table__th--sort"
            :aria-sort="ariaSort('lastHighlightedAt')"
            @click="sortByColumn('lastHighlightedAt')"
          >
            <span class="location-highlights-table__th-inner">
              Last highlighted
              <IconEmbedded v-if="sortKey === 'lastHighlightedAt'" class="location-highlights-table__caret" :name="sortCaret" :size="10" />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!visibleRows.length">
          <td class="location-highlights-table__empty-cell" colspan="5">{{ peopleVisible ? "No accounts match" : "No places or events match" }}</td>
        </tr>
        <tr v-for="row in visibleRows" :key="row.locationId" class="location-highlights-table__row">
          <td class="location-highlights-table__td">
            <div class="location-highlights-table__location">
              <LocationAvatar :src="row.icon" :name="row.name" :size="40" />
              <div class="location-highlights-table__copy">
                <div class="location-highlights-table__name-row">
                  <span class="location-highlights-table__name">{{ row.name }}</span>
                  <span v-if="row.isArchived" class="location-highlights-table__archived">Archived</span>
                  <LocationCategoryIcon :option="row.tagKind === 'Person' ? PERSON_CATEGORY_OPTION : undefined" :tagKind="row.tagKind" :category="locationFor(row)?.category" :size="26" />
                  <button v-if="locationFor(row)" type="button" class="location-highlights-table__edit" :aria-label="`Edit ${row.name}`" @click="emit('editLocation', locationFor(row)!)">
                    <IconEmbedded name="edit-pencil_2-5" :size="16" color="#667085" />
                  </button>
                </div>
                <div v-if="eventDateFor(row)" class="location-highlights-table__event-date">{{ eventDateFor(row) }}</div>
                <div class="location-highlights-table__meta">
                  <LocationSocialHandles v-if="hasSocialHandles(row)" :location="locationFor(row)" />
                  <template v-else-if="cityText(row)">
                    <span>{{ cityText(row) }}</span>
                    <a
                      v-if="locationFor(row)?.googlePlaceId"
                      class="location-highlights-table__place-link"
                      :href="googlePlaceUrl(locationFor(row)!)"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Google Maps"
                    >
                      <IconEmbedded name="map-pin_2" :size="14" color="#667085" />
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </td>
          <td class="location-highlights-table__td location-highlights-table__td--photos">
            <span v-if="row.tagKind === 'Person'" class="location-highlights-table__dash">—</span>
            <!-- TODO: wire the upload action (currently display only). -->
            <span v-else class="location-highlights-table__photos">
              {{ row.photoCount > 0 ? row.photoCount : "Add" }}
              <IconEmbedded name="upload_2" :size="16" color="#667085" />
            </span>
          </td>
          <td class="location-highlights-table__td location-highlights-table__td--action">
            <SrpButton v-if="!row.isArchived && row.tagKind !== 'Person'" color="gray" fill="outlined" size="tiny" @click="emit('newPost', row.locationId)">
              <template #icon><IconEmbedded name="plus_2-5" :size="14" /></template>
              Promote
            </SrpButton>
          </td>
          <td class="location-highlights-table__td location-highlights-table__td--posts">
            <span v-if="row.postCount > 0" class="location-highlights-table__post-chip">{{ row.postCount }}</span>
            <span v-else class="location-highlights-table__dash">—</span>
          </td>
          <td class="location-highlights-table__td location-highlights-table__td--date">
            <template v-if="lastHighlighted(row)">
              {{ lastHighlighted(row)!.date }} <span class="location-highlights-table__date-status">· {{ lastHighlighted(row)!.status }}</span>
            </template>
            <span v-else>Not yet highlighted</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="location-highlights-table__footer">
      <span>{{ visibleRows.length === availableRows.length ? `Showing all ${countLabel(availableRows.length)}` : `Showing ${visibleRows.length} of ${countLabel(availableRows.length)}` }}</span>
      <LinkWithIcon tag="button" class="location-highlights-table__add-more" is-dotted-underline color="gray" @click="emit('addLocation')">
        <template #icon><IconEmbedded name="plus_2-5" :size="14" /></template>
        Add more
      </LinkWithIcon>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import DayjsExtended from "@logic/DayjsExtended";
import { locationTextMatches } from "@logic/LocationSearch";
import { PERSON_CATEGORY_OPTION } from "@logic/LocationCategories";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import LocationCategoryDropdown from "@components/SocialLocations/LocationCategoryDropdown.vue";
import LocationCategoryIcon from "@components/SocialLocations/LocationCategoryIcon.vue";
import LocationSocialHandles from "@components/SocialLocations/LocationSocialHandles.vue";

// Types
import type { IconName } from "@contracts/IconName";
import type { LocationData } from "@contracts/socialLocations";
import type { LocationHighlightRow } from "@contracts/socialReports";

type ActiveTab = "highlighted" | "notYet" | "all";
type SortKey = "name" | "postCount" | "lastHighlightedAt" | "photoCount";
type SortDir = "asc" | "desc";

const props = defineProps<{
  customerId: string;
  rows: LocationHighlightRow[];
  peopleRows: LocationHighlightRow[];
  archivedRows: LocationHighlightRow[];
  /** Full saved locations keyed by id: socials, address, category, and the object handed to the editor. */
  locationsById: Record<string, LocationData>;
  activeTab: ActiveTab;
}>();

const emit = defineEmits<{
  (e: "update:activeTab", value: ActiveTab): void;
  (e: "addLocation"): void;
  (e: "editLocation", location: LocationData): void;
  (e: "newPost", locationId: string): void;
}>();

const search = ref<string>("");
const selectedCategory = ref<string>("");
const sortKey = ref<SortKey | null>(null);
const sortDir = ref<SortDir>("desc");
const SHOW_PEOPLE_STORAGE_KEY = "locationHighlightsShowPeople";

function showPeopleStorageKey(customerId: string): string {
  return `${SHOW_PEOPLE_STORAGE_KEY}:${customerId.trim().toLowerCase()}`;
}

function readShowPeople(customerId: string): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(showPeopleStorageKey(customerId)) === "true";
  } catch {
    return false;
  }
}

const showPeople = ref(readShowPeople(props.customerId));
const showArchived = ref(false);
const archivedVisible = computed(() => showArchived.value && props.archivedRows.length > 0);
const peopleVisible = computed(() => (showPeople.value && props.peopleRows.length > 0) || (archivedVisible.value && props.archivedRows.some(row => row.tagKind === "Person")));
watch(peopleVisible, enabled => {
  if (!enabled && selectedCategory.value === "Person") selectedCategory.value = "";
});
watch(
  () => props.customerId,
  customerId => (showPeople.value = readShowPeople(customerId))
);

function setShowPeople(value: boolean) {
  showPeople.value = value;
  try {
    window.localStorage.setItem(showPeopleStorageKey(props.customerId), String(value));
  } catch {
    // The toggle still works for this visit when browser storage is unavailable.
  }
}

function toggleShowPeople(close: () => void) {
  setShowPeople(!showPeople.value);
  close();
}

function toggleShowArchived(close: () => void) {
  showArchived.value = !showArchived.value;
  close();
}

function showAddedPerson() {
  setShowPeople(true);
}

/** Used by "View all" so the not-yet list isn't hidden behind a leftover search or type filter. */
function clearSearch() {
  search.value = "";
  selectedCategory.value = "";
}

defineExpose({ clearSearch, showAddedPerson });

function countLabel(count: number): string {
  if (peopleVisible.value) return `${count} ${count === 1 ? "account" : "accounts"}`;
  return `${count} ${count === 1 ? "place or event" : "places & events"}`;
}

// Location details ===========================================================
function locationFor(row: LocationHighlightRow): LocationData | undefined {
  return props.locationsById[row.locationId];
}

function hasSocialHandles(row: LocationHighlightRow): boolean {
  const socialSet = locationFor(row)?.socialSet;
  return Boolean(socialSet?.instagramHandle || socialSet?.facebookPageName || socialSet?.tikTokHandle);
}

function cityText(row: LocationHighlightRow): string {
  return locationFor(row)?.city?.trim() ?? "";
}

function googlePlaceUrl(location: LocationData): string {
  let uri = "https://www.google.com/maps/search/?api=1";
  uri += location.latitude != null && location.longitude != null ? `&query=${location.latitude},${location.longitude}` : `&query=${encodeURIComponent(location.name)}`;
  if (location.googlePlaceId) uri += `&query_place_id=${encodeURIComponent(location.googlePlaceId)}`;
  return uri;
}

// Sorting ====================================================================
function defaultDir(key: SortKey): SortDir {
  return key === "name" ? "asc" : "desc";
}

function sortByColumn(key: SortKey) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
    return;
  }
  sortKey.value = key;
  sortDir.value = defaultDir(key);
}

function ariaSort(key: SortKey): "ascending" | "descending" | "none" {
  if (sortKey.value !== key) return "none";
  return sortDir.value === "asc" ? "ascending" : "descending";
}

const sortCaret = computed<IconName>(() => (sortDir.value === "asc" ? "caret-top_2-5" : "caret-bottom_2-5"));

function lastHighlightedValue(row: LocationHighlightRow): number {
  if (!row.lastHighlightedAt) return Number.NEGATIVE_INFINITY;
  const date = DayjsExtended(row.lastHighlightedAt);
  return date.isValid() ? date.valueOf() : Number.NEGATIVE_INFINITY;
}

// Filters ====================================================================
const availableRows = computed<LocationHighlightRow[]>(() => {
  if (!showPeople.value && !archivedVisible.value) return props.rows;
  return [...props.rows, ...(showPeople.value ? props.peopleRows : []), ...(archivedVisible.value ? props.archivedRows : [])].sort((a, b) => {
    if (a.highlightCount !== b.highlightCount) return b.highlightCount - a.highlightCount;
    if (a.postCount !== b.postCount) return b.postCount - a.postCount;
    const aDate = lastHighlightedValue(a);
    const bDate = lastHighlightedValue(b);
    if (aDate !== bDate) return bDate - aDate;
    return (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
  });
});
const highlightedRows = computed<LocationHighlightRow[]>(() => availableRows.value.filter(r => r.postCount > 0));
const notYetRows = computed<LocationHighlightRow[]>(() => availableRows.value.filter(r => r.postCount === 0));

const tabOptions = computed<{ value: ActiveTab; label: string; count: number }[]>(() => [
  { value: "all", label: "All", count: availableRows.value.length },
  { value: "highlighted", label: "Highlighted", count: highlightedRows.value.length },
  { value: "notYet", label: "Not yet highlighted", count: notYetRows.value.length },
]);

const activeTabLabel = computed<string>(() => tabOptions.value.find(o => o.value === props.activeTab)?.label ?? "All");

function chooseTab(value: ActiveTab, close: () => void) {
  if (value !== props.activeTab) emit("update:activeTab", value);
  close();
}

const tabRows = computed<LocationHighlightRow[]>(() => {
  if (props.activeTab === "highlighted") return highlightedRows.value;
  if (props.activeTab === "notYet") return notYetRows.value;
  return availableRows.value;
});

function matchesSearch(row: LocationHighlightRow, query: string): boolean {
  const location = locationFor(row);
  const socialSet = location?.socialSet;
  return locationTextMatches([row.name, row.instagramHandle, socialSet?.facebookPageName, socialSet?.tikTokHandle, location?.address, location?.city, eventDateFor(row)], query);
}

/** An Event's date text (free text, ex. "First weekend in August"), shown under its name. Empty for other kinds. */
function eventDateFor(row: LocationHighlightRow): string {
  if (row.tagKind !== "Event") return "";
  return locationFor(row)?.excludeFromIndex?.eventDetails?.date?.trim() ?? "";
}

function matchesCategory(row: LocationHighlightRow): boolean {
  if (!selectedCategory.value) return true;
  if (selectedCategory.value === "Person") return row.tagKind === "Person";
  if (selectedCategory.value === "Event") return row.tagKind === "Event";
  return row.tagKind === "Location" && locationFor(row)?.category === selectedCategory.value;
}

// Rows are pre-sorted by the backend (highlightCount desc) until a header is clicked.
const visibleRows = computed<LocationHighlightRow[]>(() => {
  let query = search.value.trim().toLowerCase();
  if (query.startsWith("@")) query = query.slice(1);
  const filtered = tabRows.value.filter(r => matchesCategory(r) && (!query || matchesSearch(r, query)));
  if (!sortKey.value) return filtered;

  const key = sortKey.value;
  const dir = sortDir.value === "asc" ? 1 : -1;
  return [...filtered].sort((a, b) => {
    if (key === "name") return dir * (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
    if (key === "lastHighlightedAt") {
      const aHasDate = lastHighlightedValue(a) !== Number.NEGATIVE_INFINITY;
      const bHasDate = lastHighlightedValue(b) !== Number.NEGATIVE_INFINITY;
      if (aHasDate !== bHasDate) return aHasDate ? -1 : 1;
      return dir * (lastHighlightedValue(a) - lastHighlightedValue(b));
    }
    return dir * ((a[key] ?? 0) - (b[key] ?? 0));
  });
});

// Last highlighted ===========================================================
/** Null when the location has no posts in the current filters. */
function lastHighlighted(row: LocationHighlightRow): { date: string; status: string } | null {
  if (row.postCount <= 0) return null;
  const date = row.lastHighlightedAt ? DayjsExtended(row.lastHighlightedAt) : null;
  // Drafts that were never given a date still count as a highlight under the "All" post filter.
  if (!date || !date.isValid()) return { date: "No date yet", status: "Drafted" };

  const now = DayjsExtended();
  const dateText = date.year() === now.year() ? date.format("MMM D") : date.format("MMM D, YYYY");
  if (date.isAfter(now)) return { date: dateText, status: row.lastHighlightedIsApproved ? "Scheduled" : "Drafted" };
  return { date: dateText, status: row.lastHighlightedIsPublished === false ? "Drafted" : "Published" };
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Location highlights table ==================================================
.location-highlights-table {
  &__heading {
    margin-bottom: 12px;
    gap: 8px;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
  }

  &__total {
    color: #667085;
    font: 400 16px/20px sans-serif;
  }

  &__toolbar {
    margin-bottom: 14px;
    gap: 8px;
    display: grid;
    grid-template-columns: minmax(0, 320px) minmax(0, 180px) minmax(0, 180px) minmax(160px, 1fr);
    align-items: center;
  }

  &__actions {
    width: 100%;
    gap: 8px;
    display: flex;
    align-items: center;
    justify-self: stretch;
  }

  &__more-dropdown {
    margin-left: auto;
  }

  &__more-trigger {
    width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #d8dce2;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;

    &:hover {
      border-color: #087f82;
      background: #eef6f5;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 2px;
    }
  }

  &__more-menu {
    padding: 5px;

    button {
      width: 100%;
      padding: 8px 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border: 0;
      border-radius: 4px;
      background: #fff;
      color: #28343b;
      text-align: left;
      cursor: pointer;

      &:hover {
        background: #eef6f5;
      }

      &:focus-visible {
        outline: 2px solid #087f82;
        outline-offset: -2px;
      }
    }
  }

  &__search {
    height: 36px;
    box-sizing: border-box;
    padding: 0 10px;
    gap: 8px;
    display: flex;
    align-items: center;
    border: 1px solid #d8dce2;
    border-radius: 6px;
    background: #fff;

    &:focus-within {
      border-color: #087f82;
    }

    input {
      width: 100%;
      min-width: 0;
      padding: 0;
      border: 0;
      outline: 0;
      background: transparent;
      font: 400 14px/18px sans-serif;
    }
  }

  &__status {
    width: 100%;

    :deep(.dropdown-wrapper__clickable-element) {
      width: 100%;
    }
  }

  &__status-trigger {
    width: 100%;
    height: 36px;
    padding: 0 10px;
    gap: 7px;
    display: flex;
    align-items: center;
    border: 1px solid #d8dce2;
    border-radius: 6px;
    background: #fff;
    color: #28343b;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;

    span {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:hover {
      border-color: #087f82;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 2px;
    }
  }

  &__status-menu {
    padding: 5px;

    button {
      width: 100%;
      padding: 8px 10px;
      gap: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border: 0;
      border-radius: 4px;
      background: #fff;
      color: #28343b;
      text-align: left;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        background: #eef6f5;
      }

      &:focus-visible {
        outline: 2px solid #087f82;
        outline-offset: -2px;
      }
    }
  }

  &__status-menu &__status-choice--selected {
    background: #eef6f5;
  }

  &__status-count {
    color: #667085;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__header-row {
    background: #f4f7f9;
  }

  &__th {
    padding: 11px 12px;
    color: #667085;
    font: 500 13px/18px sans-serif;
    text-align: left;
    white-space: nowrap;

    &:first-child {
      padding-left: 14px;
      border-radius: 8px 0 0 0;
    }

    &:last-child {
      border-radius: 0 8px 0 0;
    }

    &--posts {
      width: 60px;
    }

    &--date {
      width: 170px;
    }

    &--photos {
      width: 90px;
    }

    &--action {
      width: 95px;
    }

    &--sort {
      cursor: pointer;

      &:hover {
        color: #102f41;
      }
    }
  }

  &__th-inner {
    gap: 5px;
    display: inline-flex;
    align-items: center;
  }

  &__caret {
    flex-shrink: 0;
  }

  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }

  &__row {
    border-bottom: 1px solid #e7e9ed;
  }

  &__td {
    padding: 16px 12px;
    color: #102f41;
    font: 400 14px/18px sans-serif;
    vertical-align: middle;

    &:first-child {
      padding-left: 14px;
    }

    &--date {
      color: #667085;
      font-size: 13px;
      white-space: nowrap;
    }

    &--action {
      white-space: nowrap;
    }
  }

  &__location {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__copy {
    min-width: 0;
    gap: 2px;
    display: grid;
  }

  &__name-row {
    gap: 8px;
    display: flex;
    align-items: center;
  }

  &__name {
    min-width: 0;
    font: 600 15px/20px $font-family;
    overflow-wrap: anywhere;
  }

  &__archived {
    padding: 2px 6px;
    border-radius: 4px;
    color: #a12828;
    background: #fce9e9;
    font: 500 11px/14px $font-family;
    white-space: nowrap;
  }

  &__edit {
    padding: 3px;
    display: inline-flex;
    border: 0;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  &__event-date {
    color: #475467;
    font: 500 13px/18px $font-family;
    overflow-wrap: anywhere;
  }

  &__meta {
    min-height: 18px;
    gap: 6px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    color: #667085;
    font-size: 13px;
  }

  &__place-link {
    display: inline-flex;
    align-items: center;

    &:hover {
      opacity: 0.75;
    }
  }

  &__post-chip {
    min-width: 28px;
    height: 28px;
    box-sizing: border-box;
    padding: 0 8px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    background: #f1f4f7;
    font-weight: 600;
  }

  &__dash {
    padding-left: 9px;
    color: #98a2b3;
  }

  &__photos {
    gap: 6px;
    display: inline-flex;
    align-items: center;
  }

  &__empty-cell {
    padding: 22px 14px;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__footer {
    margin-top: 14px;
    gap: 12px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__add-more {
    padding: 0;
    border: 0;
    background: transparent;
  }
}

// tablet -----------------------------
@media (max-width: $tablet-max-width) {
  .location-highlights-table {
    &__toolbar {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) max-content;
    }

    &__search {
      grid-column: 1 / -1;
    }
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-highlights-table {
    &__toolbar {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__actions {
      grid-column: 1 / -1;
    }

    &__th--date,
    &__td--date,
    &__th--photos,
    &__td--photos {
      display: none;
    }

    &__td {
      padding: 12px 6px;
    }

    &__name-row {
      flex-wrap: wrap;
    }

    &__name {
      flex-basis: 100%;
    }
  }
}
</style>
